import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import AppNavbar from "../components/Navbar";
import { getImageIcon, getRegionIcon } from "../utils/iconRegistry";
import api from "../services/api";
import SpacesService from "../services/providers/SpacesService";
import toast from "react-hot-toast";
import {
  HiServer,
  HiDatabase,
  HiArrowRight,
  HiCloudDownload,
  HiChartBar,
  HiGlobeAlt,
  HiFolderOpen,
  HiLightningBolt,
  HiLockClosed,
} from "react-icons/hi";
import "../styles/global.css";
import Loading from "../components/Loading";

const Dashboard = () => {
  const [droplets, setDroplets] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("servers");
  const navigate = useNavigate();

  const inFlightRef = React.useRef(false);
  const [hasProvisioning, setHasProvisioning] = useState(false);

  useEffect(() => {
    fetchDroplets();
    fetchSpaces();
  }, []);

  useEffect(() => {
    setHasProvisioning(
      droplets.some((d) => d.status === "new" || !d.ip_address)
    );
  }, [droplets]);

  useEffect(() => {
    if (!hasProvisioning) return;
    const tick = () => {
      if (document.hidden) return;
      if (inFlightRef.current) return;
      fetchDroplets();
    };
    const id = setInterval(tick, 7000);
    return () => clearInterval(id);
  }, [hasProvisioning]);

  const fetchDroplets = async () => {
    try {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setLoading(true);
      const response = await api.get("/droplets");
      setDroplets(response.data || []);
    } catch (err) {
      toast.error("Failed to load servers");
      setDroplets([]);
    } finally {
      inFlightRef.current = false;
      setTimeout(() => setLoading(false), 2500);
    }
  };

  const fetchSpaces = async () => {
    try {
      const data = await SpacesService.listSpaces();
      setSpaces(data || []);
    } catch (err) {
      console.error("Failed to load spaces:", err);
      setSpaces([]);
    }
  };

  const formatMemory = (memory) => {
    if (memory >= 1024) {
      return `${(memory / 1024).toFixed(1)} GB`;
    }
    return `${memory} MB`;
  };

  const AsyncImageIcon = ({ image }) => {
    const img = useMemo(
      () => (typeof image === "string" ? { slug: image, name: image } : image),
      [image]
    );
    const imageKey = img?.slug || img?.name;
    const [state, setState] = useState({
      type: "icon",
      Icon: null,
      src: null,
      label: "",
    });
    useEffect(() => {
      let mounted = true;
      (async () => {
        const res = await getImageIcon(img);
        if (mounted) setState(res);
      })();
      return () => (mounted = false);
    }, [imageKey, img]);
    if (state.type === "img" && state.src)
      return (
        <img
          src={state.src}
          alt={state.label}
          className="dashboard-image-icon"
        />
      );
    const I = state.Icon;
    return I ? (
      <I className="dashboard-icon" aria-label={state.label} />
    ) : (
      <></>
    );
  };

  const AsyncRegionIcon = ({ slug }) => {
    const [state, setState] = useState({
      type: "icon",
      Icon: null,
      src: null,
      label: "",
    });
    useEffect(() => {
      let mounted = true;
      (async () => {
        const res = await getRegionIcon(slug);
        if (mounted) setState(res);
      })();
      return () => (mounted = false);
    }, [slug]);
    if (state.type === "img" && state.src)
      return (
        <img src={state.src} alt={state.label} className="region-flag-icon" />
      );
    const I = state.Icon;
    return I ? <I className="region-icon" aria-label={state.label} /> : <></>;
  };

  return (
    <div className="dashboard-page">
      <AppNavbar />
      <Container>
        {/* Header */}
        <div className="dashboard-header main-container">
          <div className="dashboard-header-left">
            <h1 className="dashboard-title">Dashboard</h1>
            <p className="dashboard-subtitle">
              Manage your cloud infrastructure, servers, and storage
            </p>
          </div>
          <div className="dashboard-actions">
            <button
              className="btn-cta cta-light"
              onClick={() => navigate("/spaces/order")}
            >
              + New Storage
            </button>
            <button
              className="btn-primary btn-cta"
              onClick={() => navigate("/order")}
            >
              + New VPS
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="dashboard-stats-grid">
          <div
            className="dashboard-stat-card"
            onClick={() => {
              setActiveTab("servers");
              window.scrollTo({ top: 300, behavior: "smooth" });
            }}
          >
            <div className="dashboard-stat-number">{droplets.length}</div>
            <div className="dashboard-stat-label">
              Total Servers
              <HiArrowRight className="dashboard-stat-icon" />
            </div>
          </div>
          <div
            className="dashboard-stat-card"
            onClick={() => {
              setActiveTab("spaces");
              window.scrollTo({ top: 300, behavior: "smooth" });
            }}
          >
            <div className="dashboard-stat-number">{spaces.length}</div>
            <div className="dashboard-stat-label">
              Total Spaces
              <HiArrowRight className="dashboard-stat-icon" />
            </div>
          </div>
        </div>

        {/* Product Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`dashboard-tab ${
              activeTab === "servers" ? "active" : ""
            }`}
            onClick={() => setActiveTab("servers")}
          >
            <HiServer /> Servers
          </button>
          <button
            className={`dashboard-tab ${
              activeTab === "spaces" ? "active" : ""
            }`}
            onClick={() => setActiveTab("spaces")}
          >
            <HiDatabase /> Spaces
          </button>
        </div>

        {/* Servers Tab */}
        {activeTab === "servers" && (
          <>
            {loading ? (
              <Loading text="Loading servers..." />
            ) : droplets.length === 0 ? (
              <div className="dashboard-empty-state">
                <div className="dashboard-empty-icon">
                  <HiServer />
                </div>
                <h3 className="dashboard-empty-title">No servers yet</h3>
                <p className="dashboard-empty-desc">
                  Get started by ordering your first cloud server. Choose from
                  various configurations to match your needs.
                </p>
                <div className="dashboard-empty-actions">
                  <button
                    className="btn-primary"
                    onClick={() => navigate("/order")}
                  >
                    Order Server
                  </button>
                  <button className="btn-secondary" onClick={fetchDroplets}>
                    Refresh
                  </button>
                </div>
              </div>
            ) : (
              <div className="dashboard-product-grid">
                {droplets.map((droplet) => (
                  <div key={droplet.id} className="dashboard-product-card">
                    {(droplet.status === "new" || !droplet.ip_address) && (
                      <div className="dashboard-creating-overlay">
                        <div className="dashboard-creating-spinner"></div>
                        <div className="dashboard-creating-text">
                          Creating server...
                        </div>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className="dashboard-card-header">
                      <div className="dashboard-card-title-row">
                        <h3 className="dashboard-card-title">{droplet.name}</h3>
                        <span className="dashboard-card-id">
                          #{droplet.droplet_id}
                        </span>
                      </div>
                      <div
                        className={`dashboard-status-badge status-${droplet.status}`}
                      >
                        <div className="dashboard-status-dot"></div>
                        <span>
                          {droplet.status.charAt(0).toUpperCase() +
                            droplet.status.slice(1)}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="dashboard-card-content">
                      {/* Image Info */}
                      <div className="dashboard-card-section">
                        <div className="dashboard-info-row">
                          <div className="dashboard-info-icon">
                            <AsyncImageIcon
                              image={droplet?.image_slug.split("-")[0]}
                            />
                          </div>
                          <div className="dashboard-info-details">
                            <div className="dashboard-info-name">
                              {droplet.image}
                            </div>
                            <div className="dashboard-info-type">
                              {droplet.image?.type === "application"
                                ? "Marketplace App"
                                : "OS Image"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Specs */}
                      <div className="dashboard-card-section">
                        <div className="dashboard-specs-grid">
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">
                              IP Address
                            </div>
                            <div className="dashboard-spec-value">
                              {droplet.ip_address || "Provisioning..."}
                            </div>
                          </div>
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">Region</div>
                            <div className="dashboard-spec-value">
                              <AsyncRegionIcon slug={droplet.region} />
                              {droplet.region}
                            </div>
                          </div>
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">vCPUs</div>
                            <div className="dashboard-spec-value">
                              {droplet.vcpus}
                            </div>
                          </div>
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">Memory</div>
                            <div className="dashboard-spec-value">
                              {formatMemory(droplet.memory)}
                            </div>
                          </div>
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">Storage</div>
                            <div className="dashboard-spec-value">
                              {droplet.disk} GB SSD
                            </div>
                          </div>
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">Monthly</div>
                            <div className="dashboard-spec-value">
                              {(() => {
                                const currency =
                                  droplet?.currency ||
                                  droplet?.size_info?.currency ||
                                  "USD";
                                if (currency === "INR") {
                                  const val =
                                    typeof droplet?.size_info
                                      ?.price_monthly_inr === "number"
                                      ? droplet.size_info.price_monthly_inr
                                      : Number(
                                          droplet?.size_info
                                            ?.price_monthly_inr || 0
                                        );
                                  return `₹${val.toFixed(2)}/mo`;
                                }
                                const usd =
                                  typeof droplet?.size_info?.price_monthly ===
                                  "number"
                                    ? droplet.size_info.price_monthly
                                    : Number(
                                        droplet?.size_info?.price_monthly || 0
                                      );
                                return `₹${usd.toFixed(2)}/mo`;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      {(droplet.backup_ids?.length > 0 ||
                        droplet.monitoring ||
                        droplet.features?.includes("ipv6")) && (
                        <div className="dashboard-card-section">
                          <div className="dashboard-features">
                            {droplet.backup_ids?.length > 0 && (
                              <div className="dashboard-feature-tag">
                                <HiCloudDownload className="dashboard-feature-icon" />
                                Backups
                              </div>
                            )}
                            {droplet.monitoring && (
                              <div className="dashboard-feature-tag">
                                <HiChartBar className="dashboard-feature-icon" />
                                Monitoring
                              </div>
                            )}
                            {droplet.features?.includes("ipv6") && (
                              <div className="dashboard-feature-tag">
                                <HiGlobeAlt className="dashboard-feature-icon" />
                                IPv6
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="dashboard-card-footer">
                      <button
                        className="btn-primary btn-full"
                        onClick={() =>
                          navigate(`/server/${droplet.droplet_id}`)
                        }
                        disabled={
                          droplet.status === "new" ||
                          droplet.status === "suspended"
                        }
                      >
                        {droplet.status === "new"
                          ? "Creating..."
                          : "Manage Server"}
                      </button>
                      <div className="dashboard-card-meta">
                        Created{" "}
                        {new Date(droplet.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "spaces" && (
          <>
            {spaces.length === 0 ? (
              <div className="dashboard-empty-state">
                <div className="dashboard-empty-icon">
                  <HiDatabase />
                </div>
                <h3 className="dashboard-empty-title">No spaces yet</h3>
                <p className="dashboard-empty-desc">
                  Create your first Space for object storage. Perfect for
                  backups, static files, and media assets.
                </p>
                <div className="dashboard-empty-actions">
                  <button
                    className="btn-primary"
                    onClick={() => navigate("/spaces/order")}
                  >
                    Order Space
                  </button>
                  <button className="btn-secondary" onClick={fetchSpaces}>
                    Refresh
                  </button>
                </div>
              </div>
            ) : (
              <div className="dashboard-product-grid">
                {spaces.map((space) => (
                  <div
                    key={space.id || space.db_id}
                    className="dashboard-product-card"
                  >
                    {/* Card Header */}
                    <div className="dashboard-card-header">
                      <div className="dashboard-card-title-row">
                        <h3 className="dashboard-card-title">{space.name}</h3>
                      </div>
                      <div
                        className={`dashboard-status-badge status-${
                          space.status ?? "active"
                        }`}
                      >
                        <div className="dashboard-status-dot"></div>
                        <span>
                          {(() => {
                            const status = space.status ?? "active";
                            return (
                              status.charAt(0).toUpperCase() + status.slice(1)
                            );
                          })()}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="dashboard-card-content">
                      {/* Region Info */}
                      <div className="dashboard-card-section">
                        <div className="dashboard-info-row">
                          <div>
                            <AsyncRegionIcon slug={space.region} />
                          </div>
                          <div className="dashboard-info-details">
                            <div className="dashboard-info-name">
                              {space.region}
                            </div>
                            <div className="dashboard-info-type">Region</div>
                          </div>
                        </div>
                      </div>

                      {/* Specs */}
                      <div className="dashboard-card-section">
                        <div className="dashboard-specs-grid">
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">
                              Storage Tier
                            </div>
                            <div className="dashboard-spec-value">
                              {space.storage_tier || "Standard"}
                            </div>
                          </div>
                          <div className="dashboard-spec-item">
                            <div className="dashboard-spec-label">Endpoint</div>
                            <div className="dashboard-spec-value">
                              {space.endpoint?.split("//")[1] || "—"}
                            </div>
                          </div>
                          {space.cdn_endpoint && (
                            <div className="dashboard-spec-item">
                              <div className="dashboard-spec-label">CDN</div>
                              <div className="dashboard-spec-value">
                                Enabled
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Features */}
                      <div className="dashboard-card-section">
                        <div className="dashboard-features">
                          <div className="dashboard-feature-tag">
                            <HiFolderOpen className="dashboard-feature-icon" />
                            Object Storage
                          </div>
                          {space.cdn_endpoint && (
                            <div className="dashboard-feature-tag">
                              <HiLightningBolt className="dashboard-feature-icon" />
                              CDN Enabled
                            </div>
                          )}
                          <div className="dashboard-feature-tag">
                            <HiLockClosed className="dashboard-feature-icon" />
                            Access Control
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="dashboard-card-footer">
                      <button
                        className="btn-primary btn-full"
                        onClick={() => navigate(`/spaces/${space.name}`)}
                        disabled={space.status === "suspended"}
                      >
                        Manage Space
                      </button>
                      <div className="dashboard-card-meta">
                        Created{" "}
                        {new Date(
                          space.created_at || Date.now()
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default Dashboard;
